import { toJS } from 'mobx'
import React from 'react'

// evertel
import { SchemaProps } from '@evertel/types'
import { Card, CardBody, Row, Col } from '@evertel/web/ui'
import { ParsedRow } from './types'
import { PLACEHOLDER_ELEMENT_ID } from './constants'

interface SchemaDebugPanelProps {
    /** The raw schema from the controller (what gets saved) */
    rawSchema: SchemaProps | null
    /** The parsed rows used for UI rendering */
    parsedRows: ParsedRow[]
    /** Current schema key (root or internal schema id) */
    schemaKey: string
}

/**
 * Debug panel that displays both the raw schema and parsed UI representation side by side.
 * Only visible when SCHEMA_BUILDER_DEBUG is true in constants.ts
 */
export const SchemaDebugPanel: React.FC<SchemaDebugPanelProps> = ({
    rawSchema,
    parsedRows,
    schemaKey
}) => {
    // Filter out placeholder elements from parsed rows for cleaner display
    const cleanParsedRows = parsedRows.map(row => ({
        ...row,
        items: row.items.filter(item => item.id !== PLACEHOLDER_ELEMENT_ID)
    }))

    // Convert MobX observable to plain JS for display
    const rawSchemaJS = rawSchema ? toJS(rawSchema) : null

    return (
        <Card className="sb-debug-panel mt-4">
            <div className="card-header">
                <h5 className="mb-0">
                    Schema Debug
                    <span className="badge bg-secondary ms-2 small">
                        {schemaKey === 'root' ? 'Root Schema' : `Internal: ${schemaKey}`}
                    </span>
                </h5>
            </div>
            <CardBody>
                <Row>
                    {/* Raw Schema Column */}
                    <Col md={6}>
                        <h6 className="text-muted mb-2">
                            Raw Schema (Saved)
                        </h6>
                        <p className="text-muted small mb-2">
                            Actual schema structure. Fields: <code>#fieldId</code>, subforms: <code>&amp;listId</code>
                        </p>
                        <pre
                            className="mb-0 p-2 bg-light-subtle rounded"
                            style={{ maxHeight: '400px', overflow: 'auto', fontSize: '11px' }}
                        >
                            {JSON.stringify(rawSchemaJS, null, 2)}
                        </pre>
                    </Col>

                    {/* Parsed Rows Column */}
                    <Col md={6}>
                        <h6 className="text-muted mb-2">
                            Parsed Rows (UI)
                        </h6>
                        <p className="text-muted small mb-2">
                            UI representation. All rows are type <code>row</code>, item type determines rendering.
                        </p>
                        <pre
                            className="mb-0 p-2 bg-light-subtle rounded"
                            style={{ maxHeight: '400px', overflow: 'auto', fontSize: '11px' }}
                        >
                            {JSON.stringify(cleanParsedRows, null, 2)}
                        </pre>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    )
}
